import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { Switch } from '@mui/material';
import { MoreTime } from '@mui/icons-material';
import { AutoIgnitionState } from 'console/device/types';
import { UniversalMenu } from 'console/shared/components/menu';
import { VehicleDeviceConfig } from 'console/shared/contexts/vehicle-device/config/types';
import { CustomIcon } from 'console/shared/components/icon/custom';
import { deviceRequestAutoStart } from 'console/redux/devices/items/actions';
import AutoStartIcon from './icons/autostart.svg';
import EngineIcon from './icons/engine-turned-on.svg';
import WebastoIcon from './icons/webasto-turned-on.svg';
import { DeviceConfig } from 'console/shared/contexts/device/config/types';
import { useAction } from 'shared/hooks/redux/hooks';
export const AutoStartMenu = memo(function AutoStartMenu(props) {
    const { auto_ignition } = props;
    if (auto_ignition == null) {
        return null;
    }
    return _jsx(AutoStartMenuImpl, { ...props, auto_ignition: auto_ignition });
});
const AutoStartMenuImpl = memo(function AutoStartMenuImpl(props) {
    const { anchor, onClose, auto_ignition, auto_ignition_duration, setMenuItemCount } = props;
    const { state, configuration, available } = auto_ignition;
    const { device_id } = useContext(DeviceConfig);
    const { firmware_version } = useContext(VehicleDeviceConfig);
    const { support_continue_autostart, support_separatly_interaction } = useMemo(() => {
        const version = firmware_version?.[1];
        if (version) {
            const support_continue_autostart = version >= 236;
            const support_separatly_interaction = version >= 241 &&
                configuration.webasto &&
                configuration.engine &&
                available;
            return { support_continue_autostart, support_separatly_interaction };
        }
        return {};
    }, [firmware_version, configuration, available]);
    const autostart_active = useMemo(() => state !== AutoIgnitionState.disabled, [state]);
    const onRequestAutoStart = useAction(deviceRequestAutoStart);
    const onClickAutoStart = useCallback(() => {
        onRequestAutoStart(device_id, {
            state: autostart_active
                ? AutoIgnitionState.disabled
                : AutoIgnitionState.enabled,
        });
        onClose();
    }, [device_id, autostart_active]);
    const onActivateAutoStart = useCallback(() => {
        onRequestAutoStart(device_id, { state: AutoIgnitionState.enabled });
        onClose();
    }, [device_id]);
    const onContinueAutostart = useCallback(() => {
        onRequestAutoStart(device_id, {
            state: AutoIgnitionState.delay,
            time: (auto_ignition_duration ?? 5) * 60,
        });
        onClose();
    }, [device_id, auto_ignition_duration]);
    const onActivateOnlyEngine = useCallback(() => {
        onRequestAutoStart(device_id, { state: AutoIgnitionState.engine });
        onClose();
    }, [device_id]);
    const onActivateOnlyWebasto = useCallback(() => {
        onRequestAutoStart(device_id, { state: AutoIgnitionState.webasto });
        onClose();
    }, [device_id]);
    const available_autostart_interaction = useMemo(() => {
        if (configuration.engine && (!configuration.webasto || !available)) {
            return {
                show: available,
                label: autostart_active
                    ? 'Отменить автозапуск'
                    : 'Перейти к запуску двигателя',
                additional_view: _jsx(Switch, { size: "small", checked: autostart_active }),
                onClick: onClickAutoStart,
                icon: (_jsx(CustomIcon, { Icon: AutoStartIcon, color: autostart_active ? 'warning' : 'action' })),
            };
        }
        if (configuration.webasto && (!configuration.engine || !available)) {
            return {
                label: 'Включить подогреватель',
                show: available,
                onClick: onActivateAutoStart,
                icon: (_jsx(CustomIcon, { Icon: AutoStartIcon, color: autostart_active ? 'warning' : 'action' })),
            };
        }
        return {
            label: autostart_active ? 'Отменить автозапуск' : 'Включить автозапуск',
            show: available,
            additional_view: _jsx(Switch, { checked: autostart_active, size: "small" }),
            onClick: onClickAutoStart,
            icon: (_jsx(CustomIcon, { Icon: AutoStartIcon, color: autostart_active ? 'warning' : 'action' })),
        };
    }, [available, autostart_active, configuration, state]);
    const menu_items = useMemo(() => [
        available_autostart_interaction,
        {
            label: 'Продлить автозапуск',
            show: support_continue_autostart && state === AutoIgnitionState.enabled,
            icon: _jsx(CustomIcon, { Icon: MoreTime, color: "action" }),
            onClick: onContinueAutostart,
        },
        {
            label: 'Включить только подогреватель',
            show: support_separatly_interaction &&
                state === AutoIgnitionState.disabled,
            onClick: onActivateOnlyWebasto,
            icon: (_jsx(CustomIcon, { Icon: WebastoIcon, viewBox: "0 0 40 40", color: "action" })),
        },
        {
            label: 'Включить только двигатель',
            show: support_separatly_interaction,
            onClick: onActivateOnlyEngine,
            icon: (_jsx(CustomIcon, { Icon: EngineIcon, viewBox: "0 0 40 40", color: "action", fillOpacity: 1 })),
        },
    ], [
        state,
        configuration,
        available,
        support_continue_autostart,
        support_separatly_interaction,
        autostart_active,
    ]);
    const count_showed_elements = useMemo(() => menu_items.filter(({ show }) => !!show).length, [menu_items]);
    useEffect(() => {
        setMenuItemCount(count_showed_elements);
    }, [count_showed_elements]);
    if (count_showed_elements == 0) {
        return null;
    }
    return (_jsx(UniversalMenu, { menu_items: menu_items, opened: !!anchor, anchor: anchor, onClose: onClose }));
});
